import { Alert, Snackbar } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { getGetLatestTermQueryKey, useGetLatestTerm } from "../../../api/bpm/endpoints";
import type { TermsOfUseDto } from "../../../api/bpm/model";
import { publishGlobalTermsOfUse } from "../../../api/bpm/termsOfUse";
import TermsOfUseForm from "../../../components/Admin/TermsOfUse/TermsOfUseForm";
import ListLayout from "../../../components/Layout/ListLayout";
import { isNotFoundError } from "../../../utils/termsAccess";

interface TermsOfUseRouteAuth {
  isLoading: boolean;
  user?: {
    isAdmin?: boolean;
  };
}

export function hasTermsOfUseManagementAccess(auth: TermsOfUseRouteAuth) {
  return auth.isLoading || Boolean(auth.user?.isAdmin);
}

export function enforceTermsOfUseManagementAccess(auth: TermsOfUseRouteAuth) {
  if (!hasTermsOfUseManagementAccess(auth)) {
    throw redirect({ to: "/dashboard" });
  }
}

export const Route = createFileRoute("/__auth/admin/terms-of-use")({
  component: TermsOfUseManagementComponent,
  beforeLoad: ({ context }) => {
    enforceTermsOfUseManagementAccess(context.auth);
  },
  staticData: {
    title: "admin.termsOfUse.title",
  },
});

export function TermsOfUseManagementComponent() {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState("");

  const latestTermQuery = useGetLatestTerm(undefined, {
    query: {
      retry: false,
    },
  });

  const publishMutation = useMutation({
    mutationFn: publishGlobalTermsOfUse,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getGetLatestTermQueryKey(),
      });
      navigate({ to: "/terms" });
    },
    onError: () => {
      setErrorMessage(t("admin.termsOfUse.publishError"));
    },
  });

  const latestTermMissing = isNotFoundError(latestTermQuery.error);
  const latestTerm = latestTermMissing
    ? undefined
    : (latestTermQuery.data as unknown as TermsOfUseDto | undefined);

  return (
    <ListLayout
      title={t("admin.termsOfUse.title")}
      subtitle={t("admin.termsOfUse.subtitle")}
      error={latestTermQuery.error && !latestTermMissing ? latestTermQuery.error : undefined}
      isLoading={latestTermQuery.isLoading}
    >
      <TermsOfUseForm
        latestTerm={latestTerm}
        isPublishing={publishMutation.isPending}
        onPublish={(input) => publishMutation.mutate(input)}
      />

      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={6000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </ListLayout>
  );
}
